import React from "react";
import { getTranslation } from "../utils/localization";

interface ProductSummaryProps {
  skuDetailsMap: Record<string, { price: number; name: string }>;
  finalPrice: number;
  currency: string;
  locale?: string;
}

const normalizeTitle = (str: string) => {
  const div = document.createElement("div");
  div.innerHTML = str;
  const text = div.textContent || div.innerText || "";
  return text.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Format price with comma as thousands separator and optionally remove decimal if it's .00
 */
const formatPrice = (price: number, locale?: string): string => {
  // Check if the price has decimal places
  const hasDecimal = price % 1 !== 0;

  // Format with or without decimal places
  if (hasDecimal) {
    // With decimal places (e.g. 19.99)
    return new Intl.NumberFormat(locale || "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  } else {
    // No decimal places (e.g. 20 instead of 20.00)
    return new Intl.NumberFormat(locale || "en-US").format(Math.floor(price));
  }
};

const ProductSummary: React.FC<ProductSummaryProps> = ({
  skuDetailsMap,
  finalPrice,
  currency,
  locale,
}) => {
  const text = getTranslation(locale);

  return (
    <>
      <h3 className="mb-4 text-lg font-medium mt-4">{text.orderDetails}</h3>
      <div className="overflow-hidden bg-white sm:rounded-md my-4 shadow-md mt-4">
        <ul className="divide-y divide-gray-200 divide-dashed">
          {Object.keys(skuDetailsMap).map((sku) => (
            <li key={sku} className="px-4 py-4 sm:px-6 flex justify-between">
              <span className="font-normal">
                {normalizeTitle(skuDetailsMap[sku].name)}
              </span>
              <span className="font-bold">
                ${formatPrice(skuDetailsMap[sku].price / 100, locale)}
              </span>
            </li>
          ))}
          <li className="px-4 py-4 sm:px-6 flex justify-end">
            <div>
              {text.total}:{" "}
              <span className="font-bold">
                {currency}
                {formatPrice(finalPrice / 100, locale)}
              </span>
            </div>
          </li>
        </ul>
      </div>
    </>
  );
};

export default ProductSummary;
